import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  if (import.meta.env.DEV) console.error('Missing Supabase environment variables');
}

// ============================================
// SINGLE SUPABASE CLIENT WITH CLERK AUTH
// ============================================
//
// Uses ONE client with a custom fetch wrapper that sends the
// Clerk JWT in a custom header (x-clerk-token) on every request.
//
// WHY NOT Authorization header?
// Supabase migrated from HS256 to ECC (P-256) JWT signing keys.
// PostgREST rejects Clerk's HS256-signed JWT in Authorization header
// with 400 Bad Request. Instead, we pass the JWT via custom header
// and verify it server-side using pgjwt extension in get_clerk_user_id().
//
// SECURITY: The JWT is verified in SQL using pgjwt.verify() with the
// Clerk JWT template's HS256 secret (clerk_jwt_config). Spoofing is
// impossible without knowing that secret. (Supabase itself no longer
// trusts it: the legacy HS256 signing key was revoked 2026-10-22.)
// ============================================

// Store the current Clerk JWT token
let currentAuthToken: string | null = null;

// The guest portal's booking (or group) access token. Guests are not logged in:
// RLS policies and storage policies identify them by this token (public.guest_scope()).
let currentGuestToken: string | null = null;
// When the landlord requires an access code: the proof the server returned for
// the code the guest typed (verify_guest_access_code). The server accepts only
// proofs here, never the typed code, so codes can't be guessed through the API.
let currentGuestCode: string | null = null;

// Only the database (PostgREST) and Storage read x-guest-token. Edge Functions get
// it explicitly from the calls that need it — their CORS lists differ.
function isDataApiRequest(url: RequestInfo | URL): boolean {
  const href = typeof url === 'string' ? url : url instanceof URL ? url.href : url.url;
  return href.includes('/rest/v1/') || href.includes('/storage/v1/');
}

// Single client - anon key for PostgREST auth, custom header for user identity
export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
  },
  global: {
    fetch: async (url: RequestInfo | URL, options: RequestInit = {}) => {
      const guestToken = currentGuestToken && isDataApiRequest(url) ? currentGuestToken : null;
      if (currentAuthToken || guestToken) {
        const headers = new Headers(options.headers);
        // Send Clerk JWT in custom header (NOT Authorization - that stays as anon key)
        // Server-side get_clerk_user_id() reads and verifies this with pgjwt
        if (currentAuthToken) headers.set('x-clerk-token', currentAuthToken);
        if (guestToken) headers.set('x-guest-token', guestToken);
        if (guestToken && currentGuestCode) headers.set('x-guest-code', currentGuestCode);
        return fetch(url, { ...options, headers });
      }
      return fetch(url, options);
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

/**
 * Set the Clerk JWT token for authenticated requests
 * Call this when user logs in (from SupabaseProvider)
 */
export function setAuthToken(token: string | null): void {
  if (token === currentAuthToken) return;
  currentAuthToken = token;
}

/**
 * Get the current auth token
 */
export function getAuthToken(): string | null {
  return currentAuthToken;
}

/**
 * Set the guest portal's access token (booking or group token from the URL).
 * Call it before the portal's queries run; pass null when the portal unmounts.
 */
export function setGuestToken(token: string | null): void {
  currentGuestToken = token && /^[0-9a-f-]{36}$/i.test(token) ? token : null;
}

/**
 * Get the guest portal's access token (for Edge Function calls that need it).
 */
export function getGuestToken(): string | null {
  return currentGuestToken;
}

/**
 * Set the access-code proof (null = none / forget it).
 */
export function setGuestCode(code: string | null): void {
  // Proofs are hex, typed codes [A-Z2-9]; anything else (e.g. an autocorrected
  // "č") would make every request throw when set as a header.
  const clean = (code ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  currentGuestCode = clean || null;
}

/**
 * Get the access-code proof (for Edge Function calls that need it).
 */
export function getGuestCode(): string | null {
  return currentGuestCode;
}

/**
 * Headers that identify the guest to Edge Functions (x-guest-token, plus
 * x-guest-code = the access-code proof when the landlord requires a code).
 * Empty outside the guest portal.
 */
export function guestAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (currentGuestToken) headers['x-guest-token'] = currentGuestToken;
  if (currentGuestToken && currentGuestCode) headers['x-guest-code'] = currentGuestCode;
  return headers;
}

/**
 * Get Supabase client - returns the single client instance.
 * The custom fetch wrapper dynamically injects JWT when available.
 * Kept for backward compatibility with existing imports.
 */
export function getSupabase(): SupabaseClient {
  return supabase;
}

/**
 * Get Supabase client for a specific use case
 * With single client, both modes return the same instance.
 * The custom fetch handles token injection dynamically.
 */
export function getSupabaseClient(_mode: 'anon' | 'auth' = 'auth'): SupabaseClient {
  return supabase;
}

// ============================================
// REALTIME
// ============================================
//
// The database broadcasts (realtime.send) to private topics whose names only
// checked RPCs hand out. postgres_changes can't work in this app: realtime has
// no x-clerk-token / x-guest-token, so RLS lets nothing through.
//   chat-guest-… / chat-landlord-…  (get_guest_chat_topic / get_landlord_chat_topic)
//     new_message / message_updated → the messages row
//     typing                        → { user_type, is_typing } from the other side
//   rt-landlord-… / rt-ticket-… / rt-calendar-… / rt-forum-…  (get_realtime_topic)
//     changed → { table, booking_id? / ticket_id? / thread_id? } — no content;
//     the page re-reads through its normal API

export type RealtimeTopicEvent =
  | { event: 'new_message' | 'message_updated'; payload: Record<string, unknown> }
  | { event: 'typing'; payload: { user_type?: string; is_typing?: boolean } }
  | { event: 'changed'; payload: { table?: string; booking_id?: string; ticket_id?: string; thread_id?: string } };

type RealtimeTopicListener = (event: RealtimeTopicEvent) => void;

const REALTIME_TOPIC_EVENTS = ['new_message', 'message_updated', 'typing', 'changed'] as const;

// One channel per topic, shared by every hook listening to it (e.g. the chat
// and the typing indicator; realtime-js returns the same channel object for
// the same topic name).
const realtimeTopicSubscriptions = new Map<
  string,
  { channel: ReturnType<SupabaseClient['channel']>; listeners: Set<RealtimeTopicListener> }
>();

/** Listen to a private realtime topic. Returns the unsubscribe function. */
export function subscribeToRealtimeTopic(topic: string, listener: RealtimeTopicListener): () => void {
  let entry = realtimeTopicSubscriptions.get(topic);
  if (!entry) {
    const listeners = new Set<RealtimeTopicListener>();
    const channel = getSupabase().channel(topic);
    for (const event of REALTIME_TOPIC_EVENTS) {
      channel.on('broadcast', { event }, ({ payload }) => {
        const data = (payload ?? {}) as Record<string, unknown>;
        listeners.forEach((l) => l({ event, payload: data } as RealtimeTopicEvent));
      });
    }
    channel.subscribe();
    entry = { channel, listeners };
    realtimeTopicSubscriptions.set(topic, entry);
  }
  const current = entry;
  current.listeners.add(listener);

  return () => {
    current.listeners.delete(listener);
    if (current.listeners.size === 0 && realtimeTopicSubscriptions.get(topic) === current) {
      realtimeTopicSubscriptions.delete(topic);
      getSupabase().removeChannel(current.channel);
    }
  };
}

export default supabase;
